<script setup lang="ts">
import { ActionButton, Select, StatusTag } from '@taler/ui'
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { StarterChartTemplate } from '@/app/create-ledger'

const props = defineProps<{ open: boolean; isSubmitting: boolean; hasError: boolean }>()
const emit = defineEmits<{ submit: [name: string, template: StarterChartTemplate]; close: [] }>()

const { t } = useI18n()

const dialogRef = useTemplateRef<HTMLElement>('dialog')
const nameRef = useTemplateRef<HTMLInputElement>('name')
const name = ref('')
const template = ref<StarterChartTemplate>('PERSONAL')

const templateOptions = computed(() =>
  (['PERSONAL', 'COMPANY', 'SHARED'] as const).map((value) => ({
    value,
    label: t(`shell.createLedger.templates.${value}`),
  })),
)

watch(
  () => props.open,
  (open) => {
    if (!open) return
    name.value = ''
    template.value = 'PERSONAL'
    void nextTick(() => nameRef.value?.focus())
  },
)

function onSubmit(): void {
  const trimmed = name.value.trim()
  if (trimmed !== '') emit('submit', trimmed, template.value)
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    event.preventDefault()
    emit('close')
    return
  }
  if (event.key !== 'Tab' || dialogRef.value === null) return
  const focusable = Array.from(
    dialogRef.value.querySelectorAll<HTMLElement>('button, input, [tabindex]:not([tabindex="-1"])'),
  ).filter((el) => !el.hasAttribute('disabled'))
  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  if (first === undefined || last === undefined) return
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}
</script>

<template>
  <div
    v-if="open"
    class="fixed inset-0 z-50 flex items-center justify-center bg-[color:var(--color-text)]/40 p-[var(--space-group)]"
    @click.self="emit('close')"
  >
    <form
      ref="dialog"
      role="dialog"
      aria-modal="true"
      :aria-label="t('shell.createLedger.title')"
      class="flex w-full max-w-md flex-col gap-[var(--space-group)] rounded-[var(--radius-overlay)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-[var(--space-block)] shadow-[var(--shadow-dialog)]"
      @keydown="onKeydown"
      @submit.prevent="onSubmit"
    >
      <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
        {{ t('shell.createLedger.title') }}
      </h2>
      <p class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
        {{ t('shell.createLedger.description') }}
      </p>
      <StatusTag v-if="hasError" tone="danger" role="alert">{{ t('shell.createLedger.error') }}</StatusTag>
      <label class="flex flex-col gap-[var(--space-hair)]">
        <span class="text-[length:var(--text-step-2)] text-[color:var(--color-text)]">{{
          t('shell.createLedger.name')
        }}</span>
        <input
          ref="name"
          v-model="name"
          type="text"
          required
          maxlength="120"
          class="min-h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
        />
      </label>
      <div class="flex flex-col gap-[var(--space-hair)]">
        <span class="text-[length:var(--text-step-2)] text-[color:var(--color-text)]">{{
          t('shell.createLedger.template')
        }}</span>
        <Select
          v-model="template"
          :options="templateOptions"
          :aria-label="t('shell.createLedger.template')"
        />
      </div>
      <div class="flex justify-end gap-[var(--space-inline)]">
        <button
          type="button"
          class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-[var(--space-cell)] py-[var(--space-inline)] text-[color:var(--color-text)] transition-colors duration-[var(--motion-fast)] hover:bg-[color:var(--color-surface-raised)]"
          @click="emit('close')"
        >
          {{ t('shell.createLedger.cancel') }}
        </button>
        <ActionButton type="submit" :disabled="isSubmitting || name.trim() === ''">
          {{ t('shell.createLedger.submit') }}
        </ActionButton>
      </div>
    </form>
  </div>
</template>
